// packages/ui/src/components/ChunkLoadErrorReloader.tsx
'use client';

import { useEffect } from 'react';
import { reportClientError, toClientErrorReport } from '@tourtask/utils/client/reportClientError';

/**
 * Recovers from `ChunkLoadError`s caused by a route's JavaScript chunk failing
 * to download.
 *
 * Next.js code-splits per route and does **not** retry a chunk request that
 * fails — so on a lossy link a single dropped packet on one `*.js` chunk can
 * leave a navigation stuck forever with no recovery. This component listens for
 * the resulting error and reloads the page once, which re-fetches the document
 * and its chunks (and, after a deploy, picks up the new chunk hashes).
 *
 * Mount it once, high in the tree (e.g. the root layout). It renders nothing.
 *
 * Guarded against reload loops: it won't reload again within
 * {@link RELOAD_DEBOUNCE_MS} of a previous attempt, so a genuinely missing
 * chunk degrades to "the error surfaces" rather than an infinite refresh.
 */

const RELOAD_GUARD_KEY = 'tt:chunk-reload-at';
const RELOAD_DEBOUNCE_MS = 10_000;

const CHUNK_ERROR_RE =
  /ChunkLoadError|Loading (?:CSS )?chunk [\w-]+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i;

function isChunkLoadError(value: unknown): boolean {
  if (value instanceof Error) {
    return value.name === 'ChunkLoadError' || CHUNK_ERROR_RE.test(value.message);
  }
  if (typeof value === 'string') return CHUNK_ERROR_RE.test(value);
  return false;
}

export function ChunkLoadErrorReloader(): null {
  useEffect(() => {
    const recover = (value: unknown): void => {
      let lastAttempt = 0;
      try {
        lastAttempt = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);
      } catch {
        // sessionStorage can throw in private/blocked contexts — treat as "no
        // prior attempt" and proceed; the worst case is one extra reload.
      }

      const now = Date.now();
      if (Number.isFinite(lastAttempt) && now - lastAttempt < RELOAD_DEBOUNCE_MS) {
        // Already tried very recently — the chunk is probably gone for good
        // (e.g. a missing file), so stop reloading and let the error surface.
        // Report before giving up so "we reloaded and it still failed" is
        // visible server-side rather than just a silent generic overlay.
        reportClientError(toClientErrorReport(value, { kind: 'chunk-load-giveup' }));
        return;
      }

      try {
        sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
      } catch {
        // ignore — see above
      }
      // Report (via sendBeacon, which survives the reload) so the frequency of
      // post-deploy stale-chunk hits is measurable, then reload.
      reportClientError(toClientErrorReport(value, { kind: 'chunk-load' }));
      window.location.reload();
    };

    const onError = (event: ErrorEvent): void => {
      const value: unknown = event.error ?? event.message;
      if (isChunkLoadError(value)) recover(value);
    };
    const onRejection = (event: PromiseRejectionEvent): void => {
      if (isChunkLoadError(event.reason)) recover(event.reason);
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  return null;
}
